import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import {
  useGetAdvisorSessionsQuery,
  useLazyFetchSubscriptionDetailsQuery
} from "../../redux/queries"
import { useUserLastActivity } from "./use-user-last-activity"
import { DateRangePreset } from "../ui/activity/date-filter"
import { AdvisorSession, SubscriptionDetails } from "../../types"

const presetDays: Partial<Record<DateRangePreset, number>> = {
  today: 0,
  last_7: 7,
  last_30: 30,
  last_90: 90
}

const getDateRange = (
  preset: DateRangePreset,
  customStart: number | null,
  customEnd: number | null
): { min?: number; max?: number } => {
  if (preset === "custom" && customStart && customEnd) {
    return { min: customStart, max: customEnd }
  }
  const days = presetDays[preset]
  if (days == null) return {}
  return {
    min: DateTime.now().minus({ days }).startOf("day").toMillis()
  }
}

export const useAdvisorConversations = () => {
  const lastActivity = useUserLastActivity()
  const isIdle = Date.now() - lastActivity > 180000

  const { data: allSessions, isLoading } = useGetAdvisorSessionsQuery(
    undefined,
    { pollingInterval: 10000, skip: isIdle }
  )

  const [trigger] = useLazyFetchSubscriptionDetailsQuery()
  const fetchedIds = useRef(new Set<string>())
  const [subscriptionMap, setSubscriptionMap] = useState<
    Record<string, SubscriptionDetails>
  >({})

  useEffect(() => {
    if (!allSessions?.length) return
    const newIds = allSessions
      .map(s => s.subscriptionId)
      .filter(id => id && !fetchedIds.current.has(id))

    if (!newIds.length) return

    newIds.forEach(id => fetchedIds.current.add(id))
    Promise.all(
      newIds.map(id =>
        trigger({ id })
          .unwrap()
          .catch(() => null)
      )
    ).then(results => {
      const updates: Record<string, SubscriptionDetails> = {}
      results.forEach((data, i) => {
        if (data) updates[newIds[i]] = data
      })
      if (Object.keys(updates).length) {
        setSubscriptionMap(prev => ({ ...prev, ...updates }))
      }
    })
  }, [allSessions, trigger])

  const [searchQuery, setSearchQuery] = useState("")
  const [selectedBrandId, setSelectedBrandId] = useState("")
  const [datePreset, setDatePreset] = useState<DateRangePreset>("all")
  const [customDateStart, setCustomDateStart] = useState<number | null>(null)
  const [customDateEnd, setCustomDateEnd] = useState<number | null>(null)

  const handleDateChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      if (range === "custom" && startDate && endDate) {
        setDatePreset("custom")
        setCustomDateStart(startDate.getTime())
        setCustomDateEnd(endDate.getTime())
      } else {
        setDatePreset(range)
        setCustomDateStart(null)
        setCustomDateEnd(null)
      }
    },
    []
  )

  const handleClearDate = useCallback(() => {
    setDatePreset("all")
    setCustomDateStart(null)
    setCustomDateEnd(null)
  }, [])

  const brandOptions = useMemo(() => {
    if (!allSessions?.length) return []
    const ids = new Set(allSessions.map(s => s.brandId).filter(Boolean))
    return Array.from(ids)
  }, [allSessions])

  const sessions = useMemo(() => {
    if (!allSessions) return []
    let filtered: AdvisorSession[] = allSessions

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(s => {
        const sub = subscriptionMap[s.subscriptionId]
        if (sub) {
          const fullName = `${sub.first_name ?? ""} ${
            sub.last_name ?? ""
          }`.toLowerCase()
          const email = (sub.email ?? "").toLowerCase()
          return fullName.includes(q) || email.includes(q)
        }
        return false
      })
    }

    if (selectedBrandId) {
      filtered = filtered.filter(s => s.brandId === selectedBrandId)
    }

    const { min, max } = getDateRange(
      datePreset,
      customDateStart,
      customDateEnd
    )
    if (min || max) {
      filtered = filtered.filter(s => {
        const t = new Date(s.lastActivityAt).getTime()
        return (!min || t >= min) && (!max || t <= max)
      })
    }

    return filtered
  }, [
    allSessions,
    searchQuery,
    selectedBrandId,
    datePreset,
    customDateStart,
    customDateEnd,
    subscriptionMap
  ])

  return {
    sessions,
    isLoading,
    searchQuery,
    setSearchQuery,
    selectedBrandId,
    setSelectedBrandId,
    brandOptions,
    datePreset,
    customDateStart,
    customDateEnd,
    handleDateChange,
    handleClearDate,
    subscriptionMap
  }
}
